import { LoadingOutlined } from '@ant-design/icons'
import { useAlarmSearch } from '@services/alarm'
import { useUserColumns } from '@services/auth'
import { DeviceType } from '@services/constants'
import { isNil } from '@shared/innmaxLib/utils/webHelper'
import { Collapse, Popover, Space, Spin } from 'antd'
import cx from 'classnames'
import { any, pathOr, slice, toPairs } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import ConnectorStatusLabel from 'src/components/ConnectorStatusLabel'
import { ALARM_REPORT } from 'src/routes'
import styled, { css } from 'styled-components'
import { useDashboardContext } from '../../Provider'

const antIcon = (
  <LoadingOutlined style={{ fontSize: 14, color: '#E17567' }} spin />
)

const ConnectorCollapse = (props: { connector: any }) => {
  const { connector } = props
  const { t } = useTranslation()

  const { userColumns } = useUserColumns()

  const history = useHistory()

  const {
    refetch: refetchAlarms,
    dataSource: alarms,
    loading,
  } = useAlarmSearch()

  const connectorShowingColumns = pathOr<Record<string, boolean>>(
    {},
    [DeviceType.CONNECTOR],
    userColumns
  )

  const handleSplit = (str: string, includeStr = false) => {
    const arr = str.split('.')
    for (let i = 1; i < arr.length; i++) {
      arr[i] = arr[i].charAt(0).toLowerCase() + arr[i].slice(1)
    }
    if (includeStr) {
      arr[arr.length - 1] = `${arr[arr.length - 1]}Str`
    }
    return arr
  }

  const loadAlarmHistory = React.useCallback(() => {
    refetchAlarms({
      deviceType: DeviceType.CONNECTOR,
      deviceSeq: connector.id,
      status: 1,
      pageNum: 0,
      pageSize: 1,
    })
  }, []) //eslint-disable-line

  const renderAlarmHistory = () => {
    if (loading) {
      return (
        <div className="flex items-baseline gap-5">
          <Spin indicator={antIcon} />
          <p className="text-[#E17567]">讀取警報資訊...</p>
        </div>
      )
    }
    if (!alarms || alarms.length === 0) {
      return <></>
    }
    return (
      <div>
        {slice<any>(0, 6, alarms).map((alarm: any) => (
          <AlarmItem key={alarm.id} alarm={alarm} />
        ))}
        <div
          className="text-[#E17567] underline cursor-pointer"
          onClick={() =>
            history.push(`${ALARM_REPORT}?input=${connector?.deviceId}`)
          }>
          至警報報表查看全部警報
        </div>
      </div>
    )
  }

  React.useEffect(() => {
    loadAlarmHistory()
  }, [])

  const hasChargingInfo = any(Boolean, [
    pathOr(null, ['lprNo'], connector),
    pathOr(null, ['chargingPower'], connector),
    pathOr(null, ['chargingPercentage'], connector),
  ])

  return !isNil(connector) ? (
    <CollapseStyled defaultActiveKey={['before']}>
      <Collapse.Panel
        header={
          <div className="flex items-center justify-between ">
            <Space>
              <span>充電槍{connector.connectorId}</span>
              <span className="text-[#878787]">{connector.deviceId}</span>
            </Space>
            <ConnectorStatusLabel
              status={connector.status}
              statusStr={connector.statusStr}
              type="tag"
            />
          </div>
        }
        key={connector.connectorId}
        className="panel">
        {renderAlarmHistory()}
        <div className="content">
          <div className="text-white text-sm flex flex-col gap-[10px]">
            {hasChargingInfo && (
              <div className="border-1 border-[#A2FF38] rounded-md py-5 px-2">
                <div className="whitespace-pre-line break-words mb-5">
                  充電車牌：{pathOr('--', ['lprNo'], connector)}
                </div>
                <div className="whitespace-pre-line break-words mb-5">
                  充電即時功率：{pathOr('--', ['chargingPower'], connector)}
                </div>
                <div className="whitespace-pre-line break-words">
                  充電完成百分比{' '}
                  {pathOr('--', ['chargingPercentage'], connector)}
                </div>
              </div>
            )}
            <div className="whitespace-pre-line break-words">
              充電槍編號：{pathOr('', ['deviceId'], connector)}
            </div>
            <div className="whitespace-pre-line break-words">
              車格編號：{pathOr('', ['parkingSpaceNumber'], connector)}
            </div>
            {toPairs(connectorShowingColumns).map(([key, showing]) => {
              if (!showing) {
                return null
              }
              return (
                <div key={key} className="whitespace-pre-line break-words">
                  {t(`connector:${key}`)}:{' '}
                  {pathOr('', handleSplit(key, true), connector) ||
                    (typeof pathOr('', handleSplit(key, false), connector) ===
                    'object'
                      ? JSON.stringify(
                          pathOr('', handleSplit(key, false), connector)
                        )
                      : pathOr('', handleSplit(key, false), connector)) ||
                    pathOr('', [`${key}Str`], connector) ||
                    (typeof pathOr('', [key], connector) === 'object'
                      ? JSON.stringify(pathOr('', [key], connector))
                      : pathOr('', [key], connector))}
                </div>
              )
            })}
          </div>
        </div>
      </Collapse.Panel>
    </CollapseStyled>
  ) : null
}

const CollapseStyled = styled(Collapse)`
  color: #fff;
  border: 0px !important;
  background-color: transparent !important;

  & .panel {
    background-color: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5) !important;
    > .ant-collapse-content {
      border: 0;
      background-color: transparent;
    }
    .ant-collapse-header {
      margin: 0 -8px;
      background-color: #1b1d1d !important;
    }
    .ant-collapse-content-box {
      padding: 0px;
    }

    .content,
    .ant-collapse-header-text {
      color: #fff;
    }

    .ant-collapse-expand-icon {
      color: #fff;
    }

    .city:not(:last-child) {
      margin-bottom: 15px;
    }
  }
`

const DeviceIdLabel = styled.span`
  color: #ffcc2b;
`

export default ConnectorCollapse

const AlarmItem = ({ alarm }: any) => {
  const { setSelectedAlarm } = useDashboardContext()

  const [focus, setFocus] = React.useState(false)

  const content = (
    <div
      css={css`
        font-size: 12px;
      `}>
      <p
        css={css`
          color: #003248;
          margin-bottom: 4px;
        `}>
        {pathOr('', ['createAtStr'], alarm)}
      </p>
      <p
        css={css`
          font-weight: 500;
          margin-bottom: 4px;
          color: #e17567;
        `}>
        {pathOr('', ['alarmTypeDesc'], alarm)}
      </p>
      <p
        css={css`
          color: #003248;
          margin-bottom: 0px;
        `}>
        {pathOr('', ['extra', 'description'], alarm)}
        <label>
          &nbsp;(ErrorCode:{pathOr('-', ['extra', 'vendorErrorCode'], alarm)})
        </label>
      </p>
    </div>
  )

  return (
    <Popover
      css={css`
        border-radius: 2px;
        border: 1px solid #e17567;
        background-color: rgba(225, 117, 103, 0.2);
        margin-bottom: 5px;
      `}
      autoAdjustOverflow={false}
      overlayStyle={{
        width: 206,
        borderRadius: 4,
      }}
      overlayInnerStyle={{
        backgroundColor: '#FFF1B8',
      }}
      content={content}
      placement="left"
      onOpenChange={visible => {
        setFocus(visible)
      }}>
      <AlarmMessage
        className="camera-sidebar-trigger"
        focus={focus}
        onClick={() => {
          setSelectedAlarm(alarm)
        }}>
        <div
          className={cx('text-[11px] truncate', {
            'text-[#E17567]': !focus,
            'text-white': focus,
          })}>
          <span className="text-white mr-[2px]">
            {pathOr('', ['createAtStr'], alarm)}
          </span>
          <span className="text-[14px] mb-4 font-medium truncate">
            {pathOr('', ['alarmTypeDesc'], alarm)}
          </span>
        </div>
      </AlarmMessage>
    </Popover>
  )
}

const AlarmMessage = styled.div<{ focus?: boolean }>`
  width: 100%;
  margin: 16px 0 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #ffe8e5;
  cursor: pointer;
  ${p =>
    p.focus &&
    css`
      background-color: #e17567 !important;
      div {
        color: #fff !important;
      }
    `}

  p:nth-child(3) {
    display: -webkit-box;
    margin: 0 auto;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
